import { Button, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { capitalizeFirst, useQueryParamsData, useSearchParams } from '@moderepo/mode-app-components';
import { Entity, EntityAttributeType, FetchEntityFilters, PaginationDataSet } from '@moderepo/mode-internal-apis';
import { AppAPI } from '@moderepo/mode-apis';
import { useNavigate } from 'react-router-dom';
import { modeUIContext, removeControlPanelComp, addControlPanelComp, ControlPanelCompPriority } from '@moderepo/mode-ui-state';
import { EntitiesStatisticContainerProps, GlobalSearchBar, MaintenanceDashboardStatsContainer, MaintenanceDashboardStatsContainerProps } from '.';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { useSelectProject } from '../../utils/dataStateHooks';
import { useRitzTranslation } from '../../utils/customHooks';
import { EntityBasicViewDesignOther, ExtGrid, RouteHelper } from '..';
import { EntityBasicViewDesign } from '../entities';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        mainContent: {
            display      : 'flex',
            flexDirection: 'column',
        },

        searchBar: {
            background: theme.palette.background.default,
            width     : '100%',
        },

        statsContainer: {
            alignSelf: 'center',
            width    : '100%',
        },

        entitiesListingTitle: {
            marginBottom: theme.spacing(2),
        },

        entity: {
            cursor: 'pointer',
        },

        loadMoreButton: {
            marginTop: theme.spacing(2),
        },


        [theme.breakpoints.up('md')]: {
            entitiesListing: {
                marginTop: theme.spacing(3),
            },
        },

        [theme.breakpoints.up('lg')]: {
            entitiesListing: {
                marginTop: theme.spacing(4),
            },
        },
    };
}, { name: 'MaintenanceDashboard', index: 1 });


// The search result page size limit
const SEARCH_RESULT_PAGE_SIZE = 10;


const MAINTENANCE_DASHBOARD_CONTROL_BAR = 'Maintenance_Dashboard_Control_Bar';


export interface MaintenanceDashboardContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entitiesStatisticSettings?: MaintenanceDashboardStatsContainerProps['entitiesStatisticSettings'] | undefined;

    // Whether or not to display the control bar bar at the top of the page. Default is FALSE.
    readonly stickyControlBar?: boolean | undefined;
}


/**
 * This component is a Dashboard used for Maintenance purposed. It is a place for an agent to go to to search for issues, users, home,
 * entities, etc...
 */
export const MaintenanceDashboardContainer: React.FC<MaintenanceDashboardContainerProps> = (props: MaintenanceDashboardContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const navigate = useNavigate();

    // The collection of entities found based on the search text
    const [entities, setEntities] = useState<Entity[] | undefined>(undefined);

    // The range of the search result, we will use this data to determine if there are more entities to fetch
    const [searchResultRange, setSearchResultRange] = useState<PaginationDataSet<Entity>['range'] | undefined>(undefined);

    // Get the queryParam and see if there is a 'entitySearchText' in the URL. If there is, automatically set it as the searchText
    // and have this component fetch result for that searchText
    const { entitySearchText } = useQueryParamsData();

    // The search state e.g. searchText, currentPage number, etc...
    const { searchParams, onSearchTextChange, onPage, onNextPage } = useSearchParams<{}>({
        currentPage : 0,
        itemsPerPage: SEARCH_RESULT_PAGE_SIZE,
        searchText  : entitySearchText ?? '',
        otherFields : { },
    });


    // Create a new searchFilters object whenever one of the dependencies changed and cache it. Don't create a new object on every render
    // because it will trigger the useEffect to load data
    const searchFilters = useMemo((): FetchEntityFilters | undefined => {
        if (searchParams.searchText) {
            return {
                skip               : searchParams.currentPage * searchParams.itemsPerPage,
                limit              : searchParams.itemsPerPage,
                entityNameSubstring: searchParams.searchText,
            };
        }
        return undefined;
    }, [searchParams.currentPage, searchParams.itemsPerPage, searchParams.searchText]);



    // This useEffect will take care of fetching data whenever the searchFilters object changed.
    useEffect(() => {
        if (searchFilters) {
            AppAPI.getInstance().getEntities(props.projectId, searchFilters).then((result) => {
                // Add the newly fetched search result to the entities array
                setEntities((currentEntities) => {
                    return [
                        ...(currentEntities ?? []),
                        ...result.items,
                    ];
                });

                // Backend returns the range of the search result e.g. "vnd.tinkermode.range: 0-0/2". These values tells us whether
                // there are more result to fetch and the total. Save them so we can use them later.
                setSearchResultRange(result.range);
            }).catch(() => {
                // API call failed, set the result as empty
                setEntities([]);
                setSearchResultRange(undefined);
            });
        }
    }, [props.projectId, searchFilters]);


    /**
     * On search text changed, we will fetch entities for the new search text.
     */
    const onSearchTextValueChanged = useCallback((searchText: string) => {
        if (searchText !== searchParams.searchText) {
            // clear current result because the searchText is different therefore the previous result is invalid
            setEntities(undefined);
            // Reset the current search result page to 0
            onPage(0);
            onSearchTextChange(searchText);

            // Add the searchText and currentPage to the URL so that we can reload the page or send the URL to someone and the
            // page will show the same search result
            navigate({
                pathname: '.',
                search  : searchText ? (new URLSearchParams({
                    entitySearchText: searchText,
                })).toString() : undefined,
            }, {
                state  : { isActive: true },
                replace: true,
            });
        }
    }, [navigate, onPage, onSearchTextChange, searchParams.searchText]);



    /**
     * On entity selected, take the user to the entity details page
     */
    const onEntitySelected = useCallback((entity: Entity) => {
        RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, entity.entityId);
    }, [props.projectId]);



    /**
     * On one of the entity stats panel click, take the user to the Entities page with the statSettings.entityClasses preselected
     */
    const onStatItemClick = useCallback((
        event: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
        statSettings: EntitiesStatisticContainerProps['entitiesStatisticSettings'],
    ) => {
        RouteHelper.getInstance(navigate).gotoEntitiesPage(props.projectId, {
            viewType: statSettings.entitiesListingViewType, entityClasses: statSettings.entityClasses,
        });
    }, [navigate, props.projectId]);



    /**
     * Create a controlBar component and cache it. We will place this component in the Top Nav bar
     */
    const controlBar = useMemo(() => {
        return (
            <GlobalSearchBar
                className={classes.searchBar}
                projectId={props.projectId}
                initialValue={searchParams.searchText}
                texts={{
                    searchBoxPlaceholder: trans('entity.input.search.placeholder'),
                }}
                onSearchTextChange={onSearchTextValueChanged}
            />
        );
    }, [classes.searchBar, onSearchTextValueChanged, props.projectId, searchParams.searchText, trans]);


    /**
     * This useEffect is used for attaching the controlBar to the Top Nav bar when this component initialize and also
     * remove the controlBar when this component is destroyed
     */
    useEffect(() => {
        // Only attach the controlBar to the top nav if we want it to be sticky
        if (props.stickyControlBar) {
            uiDispatch(addControlPanelComp(MAINTENANCE_DASHBOARD_CONTROL_BAR, controlBar, ControlPanelCompPriority.HIGH));
        }

        // Return a clean up function which to remove the controlBar when the component is destroyed
        return () => {
            if (props.stickyControlBar) {
                uiDispatch(removeControlPanelComp(MAINTENANCE_DASHBOARD_CONTROL_BAR));
            }
        };
    }, [controlBar, props.stickyControlBar, uiDispatch]);


    return (
        <div className={clsx(props.className, classes.root)}>
            <div className={classes.mainContent}>
                {/**
                 * Add the controlBar as part of the container if not 'stickyControlBar'
                 */}
                {!props.stickyControlBar && (
                    controlBar
                )}

                {props.entitiesStatisticSettings && props.entitiesStatisticSettings.length > 0 && (
                    <MaintenanceDashboardStatsContainer
                        className={classes.statsContainer}
                        projectId={props.projectId}
                        entitiesStatisticSettings={props.entitiesStatisticSettings}
                        onItemClick={onStatItemClick}
                    />
                )}

                {entities && (
                    <div className={classes.entitiesListing}>
                        <Typography
                            className={classes.entitiesListingTitle}
                            gutterBottom
                            component="h3"
                            variant="h5"
                        >
                            {capitalizeFirst(trans('label.search_result', { count: searchResultRange?.total ?? entities.length }))}
                        </Typography>
                        <ExtGrid container xs_spacing={1} md_spacing={2} lg_spacing={3}>
                            {entities.map((entity) => {
                                return (
                                    <Grid
                                        item
                                        xs={12}
                                        key={entity.entityId}
                                    >
                                        <EntityBasicViewDesignOther
                                            className={classes.entity}
                                            projectId={props.projectId}
                                            selectedEntity={entity}
                                            showImage
                                            showNoImageMessage
                                            design={EntityBasicViewDesign.DESIGN_1}
                                            // Show attributes as well but don't show attributes of type Object and Array
                                            showAttributes
                                            showEntityClass
                                            excludeAttributeTypes={[EntityAttributeType.OBJECT, EntityAttributeType.ARRAY]}
                                            texts={{
                                                noImageMessage: trans('label.no_image'),
                                            }}
                                            onClick={() => {
                                                onEntitySelected(entity);
                                            }}
                                        />
                                    </Grid>
                                );
                            })}
                        </ExtGrid>
                        {searchResultRange && searchResultRange.end < searchResultRange.total - 1 && (
                            <Button
                                className={classes.loadMoreButton}
                                variant="contained"
                                color="primary"
                                onClick={onNextPage}
                            >
                                {trans('label.load_more')}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('MaintenanceDashboardContainer', MaintenanceDashboardContainer);
